"use client";
import { AnimatePresence, motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { signOut } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Link as LocaleLink } from "@/i18n/routing";
import { useSession } from "next-auth/react";
import EditProductOverlay from "@/app/[locale]/product/edit-product/components/EditProductOverlay";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useQuery } from "@tanstack/react-query";
import {
  CategorySVG,
  ControlPanelSVG,
  FarbinLogoSVG,
  SignoutSVG,
  UserSVG,
} from "./SVG";
import { PERMISSIONS } from "@/core/permissions";
import { Category } from "@/zod/category.schema";
import SignInModal from "@/app/[locale]/(auth)/signin/components/SignInModal";
import SigninOverlay from "@/app/[locale]/(auth)/signin/components/SigninOverlay";
function UserMenu() {
  const t = useTranslations("Account");
  const { data: session } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [signinOverlay, setSigninOverlay] = useState(false);
  const [isEditProductOpen, setIsEditProductOpen] = useState(false);
  const [productProducer, setProductProducer] = useState<string | undefined>(
    undefined,
  );
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const userPermissions = (session?.user as any)?.permissions ?? [];
  const canEditProducts = userPermissions.includes(PERMISSIONS.PRODUCT.EDIT);

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClick, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
    };
  }, [isOpen]);

  // categories for Product management
  const { data: categories } = useQuery({
    queryKey: ["categories", "tree"],
    queryFn: async (): Promise<Category[]> => {
      const res = await fetch("/api/categories");
      const json = await res.json();
      if (!res.ok) {
        throw new Error(json?.message || json?.error || "Failed to load categories");
      }
      return json;
    },
    staleTime: 60_000,
  });

  function findCategoryBySlugOrName(
    list: Category[] | undefined,
    key: string,
  ): Category | null {
    if (!list) return null;
    const k = key.toLowerCase();
    const stack: Category[] = [...list];
    while (stack.length) {
      const node = stack.shift()!;
      if (
        (node.slug || "").toLowerCase() === k ||
        (node.name || "").toLowerCase() === k
      )
        return node;
      if (node.children && node.children.length) stack.push(...node.children);
    }
    return null;
  }

  const partnerships = findCategoryBySlugOrName(
    categories || [],
    "solution-partner",
  );
  const partnershipChildren = partnerships?.children || [];

  // handlers
  const handleMenuButton = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSigninOverlay = () => {
    setSigninOverlay(true);
  };

  if (!session?.user) {
    return (
      <>
        <button
          type="button"
          className="flex flex-col gap-2 items-center overflow-clip"
          onClick={handleSigninOverlay}
        >
          <UserSVG className="w-6.5 mt-auto lg:w-8 rounded-sm" />
          <span className="text-sm text-slateCustom tracking-wide hidden lg:block">{t("signIn")}</span>
        </button>
        <AnimatePresence>
          {signinOverlay &&
            (isDesktop ? (
              <SignInModal
                open={signinOverlay}
                onClose={() => setSigninOverlay(false)}
              />
            ) : (
              <SigninOverlay onClose={() => setSigninOverlay(false)} />
            ))}
        </AnimatePresence>
      </>
    );
  }

  return (
    <div className="relative h-fit mt-auto lg:mt-0" ref={menuRef}>
      <div
        className="flex flex-col cursor-pointer items-center justify-center gap-2 rounded-sm lg:w-18"
        onClick={handleMenuButton}
      >
        {session?.user?.image ? (
          <Image
            src={session.user.image}
            alt="User"
            width={60}
            height={60}
            className="h-6.5 w-6.5 lg:h-8 lg:w-8 rounded-sm lg:mt-2"
          />
        ) : (
          <div className="h-6 w-6">
            <UserSVG className="w-6" />
          </div>
        )}
        <span className="text-sm text-slateCustom tracking-wide hidden lg:block">{t("discover")}</span>
      </div>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-8 right-0 z-50 mt-2 w-[85vw] space-y-2 rounded-md bg-gray-200 p-2 shadow-[1px_3px_16px_rgba(143,143,143,0.75)] md:w-xs"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {session.user?.role === "ADMIN" && (
              <div className="rounded-sm bg-white p-4">
                <h4 className="mb-4 text-sm text-gray-400">
                  Admin control panel
                </h4>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <UserMenuItem
                    href="/admin/system-managment"
                    title="Control Panel"
                    handler={handleMenuButton}
                  >
                    <div className="w-full text-xs text-white">
                      <ControlPanelSVG
                        className="h-full w-full"
                        svgColor="pinkCustom"
                      />
                    </div>
                  </UserMenuItem>
                </div>
              </div>
            )}

            {(session.user?.role === "OPERATOR" ||
              session.user?.role === "ADMIN") &&
              canEditProducts && (
                <div className="rounded-sm bg-white p-4">
                  <h4 className="mb-4 text-sm text-gray-400">
                    Product management
                  </h4>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <UserMenuItem
                      title="FARBIN"
                      handler={() => {
                        setProductProducer("FARBIN");
                        setIsEditProductOpen(true);
                        handleMenuButton();
                      }}
                    >
                      <FarbinLogoSVG className="h-full w-full" svgColor="pinkCustom" />
                    </UserMenuItem>
                    {partnershipChildren.map((child) => (
                      <UserMenuItem
                        key={child.id}
                        title={child.name}
                        handler={() => {
                          setProductProducer(child.name);
                          setIsEditProductOpen(true);
                          handleMenuButton();
                        }}
                      >
                        {child.image_url ? (
                          <Image
                            src={child.image_url}
                            alt={child.name}
                            width={48}
                            height={48}
                            className="h-full w-full rounded object-cover"
                          />
                        ) : (
                          <CategorySVG className="h-full w-full rounded-sm" svgColor="pinkCustom" />
                        )}
                      </UserMenuItem>
                    ))}
                  </div>
                </div>
              )}

            {(session.user?.role === "USER" ||
              session.user?.role === "OPERATOR" ||
              session.user?.role === "ADMIN") && (
                <div className="rounded-sm bg-white p-4">
                  <h4 className="mb-4 text-sm text-gray-400">{t("accountArea")}</h4>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <UserMenuItem
                      href="/account"
                      title={t("title")}
                      handler={handleMenuButton}
                    >
                      {session.user.image ? <Image src={session.user.image} alt={t("photo")} width={60} height={60} className="rounded-sm" /> : <UserSVG className="h-full w-full" />}
                    </UserMenuItem>
                    {/* <UserMenuItem
                      href="#"
                      title="Favorites"
                      handler={handleMenuButton}
                    >
                      <StarSVG className="h-full w-full" svgColor="pinkCustom" />
                    </UserMenuItem> */}
                    <UserMenuItem title={t("signOut")} handler={() => { handleMenuButton(); void signOut(); }}>
                      <SignoutSVG className="h-full w-full p-1" />
                    </UserMenuItem>
                  </div>
                </div>
              )}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence mode="wait" initial={false}>
        {isEditProductOpen && (
          <EditProductOverlay
            onClose={() => {
              setIsEditProductOpen(false);
              setProductProducer(undefined);
            }}
            defaultProducer={productProducer}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default UserMenu;

function UserMenuItem({
  href,
  title,
  children,
  handler,
}: {
  href?: string;
  title: string;
  children: React.ReactNode;
  handler: () => void;
}) {
  const NavigationLink = href?.startsWith("/account") ? LocaleLink : Link;
  return (
    <>
      {href && (
        <NavigationLink
          href={href}
          className="flex cursor-pointer flex-col items-center justify-start space-y-2 text-xs duration-150 hover:opacity-70"
          onClick={handler}
        >
          <div className=" flex h-8 w-8 items-center justify-center rounded-md">
            {children}
          </div>
          <span className="text-slateCustom text-center text-sm">{title}</span>
        </NavigationLink>
      )}
      {!href && (
        <button
          type="button"
          className="flex cursor-pointer flex-col items-center justify-start space-y-2 text-xs"
          onClick={handler}
        >
          <div className=" flex h-8 w-8 items-center justify-center rounded-md duration-150 hover:opacity-70">
            {children}
          </div>
          <span className="text-slateCustom text-center text-sm">{title}</span>
        </button>
      )}
    </>
  );
}
